import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Auth } from "shared/@auth/Auth";
import DataList, { DataRow } from "shared/DataList";
import Loader from "shared/Loader";
import LoadingContainer from "shared/LoadingContainer";
import PagingControls from "shared/PagingControls";
import { Tab, TabContainer } from "shared/TabContainer";
import Table from "shared/Table";
import formatDate from "shared/@utils/formatDate";
import styles from "./index.module.css";
import RefundForm from "./RefundForm";
import { useDigitalDelivery } from "./useDigitalDelivery";

export const tabs = {
  details: "details",
  royalty: "royalty",
  consolidated: "consolidated",
};

export default function ({ iboNumber, events }) {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(15);

  const { hasPermission } = Auth.useContainer();
  const { records, loading, refresh } = useDigitalDelivery({
    iboNumber,
    page,
    limit,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selected = records && records[selectedIndex];

  useEffect(() => {
    if (loading) {
      events.emit("refresh:start");
    } else {
      events.emit("refresh:end");
    }
  }, [loading]);

  // Listen for refresh events on mount and stop listening when unmounted.
  useEffect(() => {
    const listener = () => {
      refresh();
    };

    events.on("refresh", listener);

    return () => {
      events.removeListener("refresh", listener);
    };
  }, [refresh]);

  const canUpdate = hasPermission("membership.transaction.update");

  return (
    <div className={styles.container}>
      {records == null ? (
        <Loader />
      ) : (
        <>
          {records.length === 0 ? (
            <h2>No Records Found</h2>
          ) : (
            <div>
              <LoadingContainer isLoading={loading}>
                <Table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Pref. ID</th>
                      <th>Date</th>
                      <th>CC</th>
                      <th>ACH</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r, i) => {
                      return (
                        <tr
                          key={i}
                          onClick={() => {
                            setSelectedIndex(i);
                          }}
                          className={classNames(styles.resultRow, {
                            [styles.selected]: selectedIndex === i,
                          })}
                        >
                          <td>{r.id}</td>
                          <td>{r.preferenceId}</td>
                          <td>
                            {r.processDate ? (
                              formatDate(r.processDate)
                            ) : (
                              <Empty />
                            )}
                          </td>
                          <td>
                            {r.ccId && r.ccId !== -1 ? r.ccId : <Empty />}
                          </td>
                          <td>
                            {r.achId && r.achId !== -1 ? r.achId : <Empty />}
                          </td>
                          <td>{r.status || <Empty />}</td>
                          <td>{r.notes || <Empty />}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                <div className={styles.pagingControls}>
                  <PagingControls
                    page={page}
                    limit={limit}
                    onPageChange={setPage}
                    onLimitChange={setLimit}
                    disableNext={records.length < limit}
                    disabled={loading}
                  />
                </div>
              </LoadingContainer>

              <div className={styles.formContainer}>
                {canUpdate && (
                  <div className={styles.leftCol}>
                    <RefundForm iboNumber={iboNumber} transaction={selected} />
                  </div>
                )}

                {selected && (
                  <div className={styles.rightCol}>
                    <TabContainer>
                      <Tab title="Details" value={tabs.details}>
                        <DataList>
                          <DataRow label="Transaction ID">
                            {selected.id}
                          </DataRow>
                          <DataRow label="Preference ID">
                            {selected.preferenceId}
                          </DataRow>
                          <DataRow label="Transaction Status">
                            {selected.status}
                          </DataRow>
                          <DataRow label="Process Date">
                            {selected.processDate ? (
                              formatDate(selected.processDate)
                            ) : (
                              <Empty />
                            )}
                          </DataRow>
                          <DataRow label="Amount Actually Paid">
                            {selected.amountActuallyPaid}
                          </DataRow>
                          <DataRow label="Digital Fee">
                            {selected.digitalFee}
                          </DataRow>
                          <DataRow label="Tax">{selected.WATax}</DataRow>
                          {/* <DataRow label="Balance/Credit"></DataRow>
                          <DataRow label="Billed To"></DataRow> */}
                          <DataRow label="AH Trace">
                            {selected.ahTraceId}
                          </DataRow>
                          <DataRow label="HC Trace">
                            {selected.hcTraceId}
                          </DataRow>
                          <DataRow label="Notes">
                            {selected.notes || <Empty />}
                          </DataRow>
                        </DataList>
                      </Tab>

                      <Tab
                        title="Royalty"
                        value={tabs.royalty}
                        badge={selected.royalty != null}
                      >
                        <span>
                          No royalty information for this transaction.
                        </span>
                      </Tab>

                      <Tab
                        title="Consolidated"
                        value={tabs.consolidated}
                        badge={selected.consolidatedBilling != null}
                      >
                        {selected.consolidatedBilling ? (
                          <Table>
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>Transaction ID</th>
                                <th>Amount</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selected.consolidatedBilling.map((record, i) => (
                                <tr key={i}>
                                  <td>{record.type}</td>
                                  <td>{record.tranId}</td>
                                  <td>{record.amount}</td>
                                  <td>
                                    <strong>{record.cnsAmount}</strong>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <span>
                            No consolidated billing for this transaction.
                          </span>
                        )}
                      </Tab>
                    </TabContainer>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Empty() {
  return <span className={styles.emptyPlaceholder}>---</span>;
}
